import React, { useEffect, useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import axios from 'axios';
import { useAtom, useAtomValue } from 'jotai';
import PropTypes from 'prop-types';
import { isDrawingState, selectedParcellesIduState, validatedParcellesState } from '@/store';
import theme from '@/theme';
import { featureCollection } from '@/utils/geometry/helpers';

async function getParcelFcByIduList(iduList) {
  return axios
    .post(
      `${import.meta.env.VITE_CADASTRE_API_URL}/parcelles/idu`,
      { idu_list: iduList },
      {
        headers: {
          'ns-api-key': import.meta.env.VITE_CADASTRE_API_KEY,
        },
      },
    )
    .then((res) => res.data)
    .catch(() => {});
}

async function getCityByInseeCode(insee) {
  return axios
    .get(`https://geo.api.gouv.fr/communes/${insee}`)
    .then((response) => response.data)
    .catch(() => {});
}

export default function ValidateParcelle({ map }) {
  const mapElt = map.getTargetElement();

  const [validatedParcelles, setValidatedParcelles] = useAtom(validatedParcellesState);

  const selectedParcellesIdu = useAtomValue(selectedParcellesIduState);

  const isDrawing = useAtomValue(isDrawingState);

  const [disableValidation, setDisableValidation] = useState(true);

  useEffect(() => {
    setDisableValidation(isDrawing || selectedParcellesIdu.length === 0);
  }, [isDrawing, selectedParcellesIdu]);

  const onClick = async () => {
    const cursor = mapElt.style.cursor;
    mapElt.style.cursor = 'wait';

    // Retrieve insee code from idu list and remove duplicate code
    const cityUniqueInseeSet = new Set(selectedParcellesIdu.map((parcelIdu) => parcelIdu.substring(5, 0)));

    // Retrieve city name from insee code
    const cityInfosPromisesList = [...cityUniqueInseeSet].map(async (cityInsee) => {
      const city = await getCityByInseeCode(cityInsee);
      return {
        name: city?.nom ? city.nom : '',
        insee: cityInsee,
      };
    });

    const cityInfos = await axios.all(cityInfosPromisesList);

    // Retrieve parcel feature collection from idu list
    const parcelFc = await getParcelFcByIduList(selectedParcellesIdu);

    if (!parcelFc?.features) {
      return;
    }

    const newFeatures = parcelFc.features.map((parcelFeature) => {
      const parcelCityInfo = cityInfos.find((cityInfo) => cityInfo.insee === parcelFeature.properties.commune);
      parcelFeature.properties.commune_nom = parcelCityInfo?.name;
      return parcelFeature;
    });

    setValidatedParcelles(featureCollection([...newFeatures, ...validatedParcelles.features]));

    mapElt.style.cursor = cursor;
  };

  return (
    <Paper
      elevation={4}
      sx={{
        p: '2px',
        backgroundColor: disableValidation ? 'grey.50' : theme.palette.secondary.main,
      }}
    >
      <IconButton
        aria-label="select-parcel"
        title="Valider la sélection"
        disabled={disableValidation}
        onClick={onClick}
      >
        <CheckIcon style={{ color: disableValidation ? null : '#fff' }} />
      </IconButton>
    </Paper>
  );
}

ValidateParcelle.propTypes = {
  map: PropTypes.object.isRequired,
};
